import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDatepicker } from '@angular/material/datepicker';
import { MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import moment from 'moment';
import { AuthService } from 'src/app/component/shared/services/auth.service';
import { CommonService } from 'src/app/component/shared/services/common.service';
import { environment } from 'src/environments/environment';
import { COLUMN_LIST, DISPLAY_COLUMN } from '../constants';
import { saveAs } from 'file-saver';

export interface SelectType {
  label: string;
  value: string;
}

export const REPORT_TYPES: SelectType[] = [
  {
    label: 'Daily Trip Performance Summary',
    value: 'daily/summary',
  },
  {
    label: 'Daily Trip Performance Detail',
    value: 'daily/detail',
  },
  {
    label: 'Daily Absent Trip Info',
    value: 'daily/absent',
  },
  {
    label: 'Monthly Trip Performance',
    value: 'monthly',
  },
];

@Component({
  selector: 'app-trip-performance',
  templateUrl: './trip-performance.component.html',
  styleUrls: ['./trip-performance.component.scss'],
})
export class TripPerformanceComponent implements OnInit, AfterViewInit {
  constructor(
    private authService: AuthService,
    private commonService: CommonService,
    private snackbar: MatSnackBar
  ) {
    this.dataSource = new MatTableDataSource<any>();
    this.dataSource.sortData = this.sortList;
  }
  pageData = {
    id: 11,
    name: 'tripPerformanceReports',
    displayPageName: 'Trip Performance Reports',
  };
  isNewWindow: boolean = false;
  isHasAccess: boolean = false;
  rightsError: string = '';

  ngOnInit() {
    setTimeout(() => {
      this.isHasAccess = this.authService.isHasAccess(this.pageData);
      this.rightsError = "You don't have access rights to this module.";
    }, 500);
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
    this.sort.sort({
      id: 'fileName',
      start: 'desc',
      disableClear: true,
    });
  }

  reportTypes: SelectType[] = REPORT_TYPES;

  form = new FormGroup({
    reportType: new FormControl('daily/summary', [Validators.required]),
    monthDate: new FormControl(moment().utcOffset(environment.timezone), []),
    yearDate: new FormControl(moment().utcOffset(environment.timezone), []),
  });
  isLoading = false;
  isError = false;

  todayDate = moment();
  onChosenYearHandler(normalizedYear: moment.Moment) {
    const monthYear = this.form.get('monthDate').value;
    const ctrlValue = monthYear;

    ctrlValue.year(normalizedYear.year());
    this.form.get('monthDate').setValue(ctrlValue);
  }

  onChosenYearOnlyHandler(
    normalizedYear: moment.Moment,
    datepicker: MatDatepicker<moment.Moment>
  ) {
    datepicker.close();
    this.form.get('yearDate').setValue(normalizedYear, { emitEvent: false });
  }

  onChosenMonthHandler(
    normalizedMonth: moment.Moment,
    datepicker: MatDatepicker<moment.Moment>
  ) {
    datepicker.close();
    const monthYear = this.form.get('monthDate').value;
    const ctrlValue = monthYear;
    ctrlValue.month(normalizedMonth.month());
    ctrlValue.year(normalizedMonth.year());

    this.form.get('monthDate').setValue(ctrlValue);
  }

  onSearch() {
    this.fetchList();
  }

  listTitle = '';
  fetchList() {
    if (this.isLoading) return;
    const { reportType, monthDate, yearDate } = this.form.value;
    if (reportType) {
      let bodyData = {};

      if (reportType !== 'monthly') {
        if (!monthDate) {
          this.snackbar.open('Please select a date.', null, {
            duration: 5000,
            panelClass: 'custom-snack-bar-panel-error',
          });
          return;
        }
        const selectedMonth = moment(monthDate).format('MM');
        const selectedYear = moment(monthDate).format('YYYY');
        bodyData = {
          year: selectedYear,
          month: selectedMonth,
        };
      } else if (reportType === 'monthly') {
        if (!yearDate) {
          this.snackbar.open('Please select a date.', null, {
            duration: 5000,
            panelClass: 'custom-snack-bar-panel-error',
          });
          return;
        }
        const selectedYear = moment(yearDate).format('YYYY');
        bodyData = {
          year: selectedYear,
        };
      }

      const searchReportUrl =
        environment.apiUrl.transit +
        `/v1/tripPerformanceReportList/${reportType}`;
      this.commonService
        .commonPostFullUrlAction(searchReportUrl, bodyData)
        .subscribe({
          next: data => {
            const { type, tripPerformanceReportPackageResp = [] } = data;

            if (
              tripPerformanceReportPackageResp &&
              tripPerformanceReportPackageResp.length > 0
            ) {
              this.listTitle =
                REPORT_TYPES.find(type => type.value === reportType)?.label ??
                type;

              this.dataSource.data = tripPerformanceReportPackageResp;
              this.dataPage = JSON.parse(
                JSON.stringify(tripPerformanceReportPackageResp)
              );
              // this.isLoading = false;
            } else {
              this.snackbar.open('No reports found.', null, {
                duration: 5000,
              });
            }

            this.isLoading = false;
          },
          error: err => {
            console.log(err);
            this.snackbar.open(
              'Failed to fetch reports. Please try again.',
              null,
              {
                duration: 5000,
                panelClass: 'custom-snack-bar-panel-error',
              }
            );
            this.reset();
            this.isLoading = false;
          },
        });
    }
  }

  reset() {
    this.dataSource.data = [];
    this.listTitle = '';
  }

  dataSource;
  dataPage;
  @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: false }) sort: MatSort;

  displayedColumns = DISPLAY_COLUMN;
  columnList = COLUMN_LIST;

  /** Sort and Filter Functions */
  sortList(records: any[], sort: MatSort): any[] {
    const { active, direction } = sort;
    if (!active || direction === '') {
      return records;
    }
    return records.sort((a: any, b: any) => {
      let comparisonResult = 0;
      switch (active) {
        case 'uploadedDatetime':
          let timeA: moment.Moment = moment(a[active]).utcOffset(
            environment.timezone
          );
          let timeB: moment.Moment = moment(b[active]).utcOffset(
            environment.timezone
          );
          if (timeA.isValid() && !timeB.isValid()) return -1;
          if (!timeA.isValid() && timeB.isValid()) return 1;
          if (timeA.isSame(timeB)) return 0;
          comparisonResult = timeA.isAfter(timeB) ? 1 : -1;
          break;
        default:
          comparisonResult = a[active].localeCompare(b[active]);
      }
      return comparisonResult * (direction === 'asc' ? 1 : -1);
    });
  }

  trackItem(index, item) {
    return item.id;
  }

  downloadReport(record) {
    const { id, fileName } = record;
    const downloadReportUrl =
      environment.apiUrl.transit + `/v1/downloadTripPerformanceReport/${id}`;
    this.commonService
      .commonGetFullUrlAction(downloadReportUrl, 'text')
      .subscribe(
        (respData: any) => {
          // const file = new File([respData], fileName, {
          //   type: 'text/csv;charset=utf-8',
          // });
          // prepend character to fix BOM issue
          const blob = new Blob([String.fromCharCode(0xfeff), respData], {
            type: 'text/csv;charset=utf-8',
          });
          saveAs.saveAs(blob, fileName, { autoBom: true });
        },
        (errorResp: HttpErrorResponse) => {
          console.error('Failed to download: ', errorResp);
          this.snackbar.open(
            'Failed to download report. Please try again.',
            null,
            {
              duration: 5000,
              panelClass: 'custom-snack-bar-panel-error',
            }
          );
        }
      );
  }
}
